import { useEffect } from "react";
import { useLang } from "../i18n";

/**
 * Page SEO: <title>, meta description, canonical URL, robots, Open Graph /
 * Twitter tags and JSON-LD structured data (schema.org). Every public page
 * calls useSeo; leaving the page restores what index.html had.
 *
 * VITE_SITE_URL (.env) is the public address used in canonical URLs and
 * structured data, e.g. https://lecoinvtc.com (defaults to the current origin).
 */

export const SITE_NAME = "Le Coin VTC";
export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string | undefined) || window.location.origin).replace(/\/+$/, "");
export const absoluteUrl = (path: string) => (/^https?:\/\//i.test(path) ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`);

type JsonLd = Record<string, unknown>;

export interface SeoOptions {
  /** Page title; " | Le Coin VTC" is added unless `fullTitle` */
  title: string;
  fullTitle?: boolean;
  description?: string;
  /** Path of the page ("/vehicles"); the canonical URL unless `canonical` is given */
  path?: string;
  canonical?: string | null;
  /** Share title / description when different from the page's (blog articles) */
  ogTitle?: string;
  ogDescription?: string;
  /** Share image (absolute or site path) */
  image?: string | null;
  type?: "website" | "article" | "product" | "profile";
  /** Login, sign-up and account pages: kept out of search results */
  noindex?: boolean;
  nofollow?: boolean;
  /** schema.org objects, written as one <script type="application/ld+json"> each */
  jsonLd?: JsonLd | JsonLd[] | null;
}

/** Sets a <meta> (by name or property), returning how to undo it */
function setMeta(attr: "name" | "property", key: string, content: string): () => void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) {
    const previous = el.content;
    el.content = content;
    return () => {
      el!.content = previous;
    };
  }
  el = document.createElement("meta");
  el.setAttribute(attr, key);
  el.content = content;
  document.head.appendChild(el);
  return () => el!.remove();
}

function setCanonical(href: string): () => void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (el) {
    const previous = el.href;
    el.href = href;
    return () => {
      el!.href = previous;
    };
  }
  el = document.createElement("link");
  el.rel = "canonical";
  el.href = href;
  document.head.appendChild(el);
  return () => el!.remove();
}

/** JSON for a <script>: "<" escaped so page text can never close the tag */
const serialize = (data: JsonLd) => JSON.stringify(data).replace(/</g, "\\u003c");

/** Pass null while the page's data is loading: nothing changes until then. */
export function useSeo(seo: SeoOptions | null) {
  const lang = useLang();
  const key = JSON.stringify([seo, lang]);

  useEffect(() => {
    if (!seo) return;
    const undo: (() => void)[] = [];
    const prevTitle = document.title;
    const title = seo.fullTitle ? seo.title : `${seo.title} | ${SITE_NAME}`;
    document.title = title;
    undo.push(() => {
      document.title = prevTitle;
    });

    const url = seo.canonical || (seo.path !== undefined ? absoluteUrl(seo.path) : SITE_URL + window.location.pathname);
    const image = seo.image ? absoluteUrl(seo.image) : null;
    const description = seo.description?.trim() ?? "";

    if (description) undo.push(setMeta("name", "description", description));
    undo.push(setMeta("name", "robots", `${seo.noindex ? "noindex" : "index"}, ${seo.nofollow ? "nofollow" : "follow"}${seo.noindex ? "" : ", max-image-preview:large"}`));
    if (!seo.noindex) undo.push(setCanonical(url));

    undo.push(setMeta("property", "og:site_name", SITE_NAME));
    undo.push(setMeta("property", "og:type", seo.type === "article" ? "article" : seo.type === "profile" ? "profile" : "website"));
    const shareTitle = seo.ogTitle || title;
    const shareDescription = seo.ogDescription?.trim() || description;
    undo.push(setMeta("property", "og:title", shareTitle));
    undo.push(setMeta("property", "og:url", url));
    undo.push(setMeta("property", "og:locale", lang === "fr" ? "fr_FR" : "en_GB"));
    if (shareDescription) undo.push(setMeta("property", "og:description", shareDescription));
    if (image) undo.push(setMeta("property", "og:image", image));
    undo.push(setMeta("name", "twitter:card", image ? "summary_large_image" : "summary"));
    undo.push(setMeta("name", "twitter:title", shareTitle));
    if (shareDescription) undo.push(setMeta("name", "twitter:description", shareDescription));
    if (image) undo.push(setMeta("name", "twitter:image", image));

    const blocks = seo.jsonLd ? (Array.isArray(seo.jsonLd) ? seo.jsonLd : [seo.jsonLd]) : [];
    for (const data of blocks) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "";
      script.textContent = serialize(data);
      document.head.appendChild(script);
      undo.push(() => script.remove());
    }

    // Undo in reverse, so a tag changed twice gets its original value back
    return () => undo.reverse().forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for seo and lang
  }, [key]);
}

/* ---------- schema.org building blocks ---------- */

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

export const organizationLd = (description: string): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: absoluteUrl("/favicon.svg"),
  description,
  email: "support@lecoinvtc.com",
  areaServed: { "@type": "AdministrativeArea", name: "Île-de-France" },
});

export const websiteLd = (lang: string): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: lang,
  publisher: { "@id": ORGANIZATION_ID },
  // Search box in Google results: the vehicle search by city
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/vehicles?city={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
});

/** [["Home", "/"], ["Vehicles", "/vehicles"], ["Tesla Model 3"]] — the last one is the page itself */
export const breadcrumbLd = (items: [string, string?][]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    ...(path !== undefined && { item: absoluteUrl(path) }),
  })),
});

export const faqLd = (items: { q: string; a: string }[]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

/** A plain page (legal, contact…) described as part of the site */
export const webPageLd = (type: string, name: string, description: string, path: string, lang: string): JsonLd => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  description,
  url: absoluteUrl(path),
  inLanguage: lang,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  publisher: { "@id": ORGANIZATION_ID },
});
